import React, { useState, useRef, useEffect } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "../utils/api";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import Loading from "./Loading";
import { VEHICLE_TYPE_OPTIONS } from "../utils/mapNevdisBodyType";
import { formatMoney, resolveSignupLinkPrices } from "../utils/signupLinkPrice";
import { canManageSignupLinks } from "../utils/signupLinkAccess";

const SelfSignupModal = ({ 
  isOpen, 
  onClose, 
  product, 
  handleEditLink, 
  onStatusUpdated,
  partnerProducts
}) => {
  const [generatedLink, setGeneratedLink] = useState("");
  const [linkData, setLinkData] = useState(null);
  const [qrCode, setQrCode] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const linkRef = useRef(null);

  // Validate email format
  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

  // Validate phone number (basic check for digits only)
  const isValidPhone = (phone) => /^\d{8,15}$/.test(phone);

  // Use the selected link as-is (do not regenerate — that can match a sibling
  // link with the same product/RRP but different lock-in / paidMonthly).
  const loadSelectedSignupLink = () => {
    if (!product || !product._id) {
      console.warn("Invalid product data.");
      setGeneratedLink("");
      setLinkData(null);
      setQrCode(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLinkData(product);
    if (product.link) {
      setGeneratedLink(product.link);
      setQrCode(true);
    } else {
      setGeneratedLink("");
      setQrCode(false);
      showErrorToast("This sign-up link has no URL. Edit and save it to regenerate.");
    }
    setLoading(false);
  };

  // Send SMS
  const handleSendSMS = async () => {
    if (!isValidPhone(phoneNumber)) {
      showErrorToast("Please enter a valid phone number.");
      return;
    }
    if (!generatedLink) {
      showErrorToast("Please wait for the signup link to generate first.");
      return;
    }

    let idPartnerUser = sessionStorage.getItem("currentUser") || "";
    idPartnerUser = JSON.parse(idPartnerUser)?._id || null;

    try {
      const response = await api("v1/selfsignup/send", "POST", { 
        idSelfSignupLink: linkData._id, 
        channel: "sms", 
        phone: phoneNumber, 
        idPartnerUser 
      });

      if (response.data.success) {
        showSuccessToast("Successfully sent SMS with Sign-Up Link!");
        setPhoneNumber("");
      } else {
        showErrorToast(response?.data?.message || "Error sending sign-up link via SMS.");
      }
    } catch (error) {
      console.error("Error sending SMS:", error);
      showErrorToast("Error sending sign-up link via SMS.");
    }
  };

  // Send Email
  const handleSendEmail = async () => {
    if (!isValidEmail(email)) {
      showErrorToast("Please enter a valid email.");
      return;
    }
    if (!generatedLink) {
      showErrorToast("Please wait for the signup link to generate first.");
      return;
    }

    let idPartnerUser = sessionStorage.getItem("currentUser") || "";
    idPartnerUser = JSON.parse(idPartnerUser)?._id || null;

    try {
      const response = await api("v1/selfsignup/send", "POST", { 
        idSelfSignupLink: linkData._id, 
        channel: "email", 
        email, 
        idPartnerUser 
      });

      if (response.data.success) {
        showSuccessToast("Successfully sent Email with Sign-Up Link!");
        setEmail("");
      } else {
        showErrorToast(response?.data?.message || "Error sending sign-up link via email.");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      showErrorToast("Error sending sign-up link via email.");
    }
  };

  // Handle Copy Link
  const handleCopyLink = () => {
    if (linkRef.current) {
      navigator.clipboard.writeText(generatedLink);
      showSuccessToast("Link copied to clipboard!");
    }
  };

  // Handle Download QR Code
  const handleDownloadQrCode = () => {
    const canvas = document.getElementById("qrCodeCanvas");
    if (canvas) {
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = "signup_qrcode.png";
      link.click();
      showSuccessToast("QR Code downloaded successfully!");
    }
  };

  const handleToEdit = () => {
    handleEditLink(product)
  }

  const handleToggleStatus = async () => {
    if (!product?._id) return;
    setUpdatingStatus(true);
  
    try {
      const response = await api(`v1/selfsignup/${product._id}/toggle-status`, "PUT", null);
      if (response.data.success) {
        showSuccessToast(`Sign-up link ${response.data.signupLink.status}!`);
        onStatusUpdated();
        setLinkData({ ...linkData, status: response.data.signupLink.status });
      } else {
        showErrorToast("Failed to update status.");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      showErrorToast("Error updating status.");
    }
    setUpdatingStatus(false);
  };
  

  useEffect(() => {
    if (!product) {
      setGeneratedLink("");
      setLinkData(null);
      setQrCode(false);
      setLoading(false);
      return;
    }

    loadSelectedSignupLink();
  }, [product]);

  const lockInLabel =
    product?.lockInPeriodMonths != null
      ? `${product.lockInPeriodMonths}-month lock-in`
      : "No lock-in";
  const billingLabel =
    product?.paidMonthly ||
    (product?.lockInPeriodMonths != null &&
      Number(product.lockInPeriodMonths) < 12)
      ? "Paid monthly"
      : "Product billing";
  const vehicleTypeLabel =
    product?.vehicleType &&
    (VEHICLE_TYPE_OPTIONS.find(
      (o) => o.value === String(product.vehicleType).toLowerCase()
    )?.label ||
      product.vehicleType);
  const { effectivePrice, merchantPrice, priceOverride } = resolveSignupLinkPrices(
    product,
    partnerProducts
  );
  const effectiveLabel = formatMoney(effectivePrice);
  const merchantLabel = formatMoney(merchantPrice);

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${isOpen ? "block" : "hidden"} z-10`}>
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full p-6 max-h-[85%] overflow-y-scroll">
        <div className="flex justify-between items-start border-b pb-3">
          <div className="w-full">
            {product?.linkLabel ? (
              <p className="text-sm text-gray-600 mb-1">{product.linkLabel}</p>
            ) : null}
            <p>Product: <strong>{product?.idSubscription ? product?.idSubscription?.name : product?.idProduct?.name || "N/A"}</strong></p>
            <div className="flex flex-row gap-2 flex-wrap">
                <p>RRP: $<strong>{product?.rrp || "0.00"}</strong></p>
                <p>
                  Product price: $
                  <strong>{effectiveLabel || "0.00"}</strong>
                  {priceOverride != null && merchantLabel ? (
                    <span className="font-normal text-gray-600"> (dealer ${merchantLabel})</span>
                  ) : null}
                </p>
            </div>
            <div className="flex flex-row gap-3 text-sm text-gray-700 mt-1 flex-wrap">
              {vehicleTypeLabel ? <span>{vehicleTypeLabel}</span> : null}
              <span>{lockInLabel}</span>
              <span>{billingLabel}</span>
            </div>
            {canManageSignupLinks() && (
                <div className="flex flex-row gap-2 mt-2">
                    <button className="bg-blue-500 text-white px-4 py-2 rounded" onClick={handleToEdit}>Edit Payment Link</button>
                    <button
                        className={`px-4 py-2 rounded ${linkData?.status === "Active" ? "bg-red-500" : "bg-green-500"} text-white`}
                        onClick={handleToggleStatus}
                        disabled={updatingStatus}
                        >
                        {updatingStatus ? "Updating..." : linkData?.status === "Active" ? "Disable Link" : "Enable Link"}
                    </button>
                </div>
            )}
            </div>
            <button className="text-gray-500 hover:text-gray-700 text-[40px] mt-[-20px]" onClick={onClose}>&times;</button>    
        </div>

        <div className="mt-4 flex flex-row items-start gap-2">
          {generatedLink ? (
            <div className="bg-gray-100 p-4 rounded-md w-3/5">
              {/* SMS */}
              <div className="bg-white p-4 shadow-sm rounded">
                <h6 className="font-semibold">SMS this link</h6>
                <div className="flex gap-2">
                  <input type="text" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} className="border rounded px-3 py-2 w-full" placeholder="Enter phone number" />
                  <button className="bg-blue-500 text-white px-4 py-2 rounded" onClick={handleSendSMS}>Send</button>
                </div>
              </div>

              {/* Email */}
              <div className="bg-white p-4 shadow-sm rounded">
                <h6 className="font-semibold">Email this link</h6>
                <div className="flex gap-2">
                  <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} className="border rounded px-3 py-2 w-full" placeholder="Enter email" />
                  <button className="bg-blue-500 text-white px-4 py-2 rounded" onClick={handleSendEmail}>Send</button>
                </div>
              </div>

              {/* Hyperlink */}
              <div className="bg-white p-4 shadow-sm rounded">
                <h6 className="font-semibold">Hyperlink</h6>
                <p>You can share this link if preferred.</p>
                <div className="flex flex-col gap-2">
                  <span ref={linkRef} className="break-all bg-gray-200 px-4 py-2 rounded">{generatedLink}</span>
                  <button className="bg-blue-500 text-white px-4 py-2 rounded" onClick={handleCopyLink}>
                    Copy
                  </button>
                </div>
              </div>
            </div>
          ) : loading ? <Loading /> : null}

          {/* QR Code + Download */}
          {qrCode && (
            <div className="flex flex-col items-center gap-2 w-2/5">
                <h5 className="font-semibold">QR Code</h5>
                <QRCodeCanvas id="qrCodeCanvas" value={generatedLink} size={256} />
                <button className="bg-blue-500 text-white px-4 py-2 rounded mt-3" onClick={handleDownloadQrCode}>Download QR Code</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelfSignupModal;
